import type { Goal, Task, SubTask, ChecklistItem as LegacyChecklistItem } from '../../types/timeline'
import type {
  DetailedNodeKind,
  DetailedSelection,
  NodeDiffStatus,
  TreeNode,
} from './types'

/**
 * Shallow equality across the scalar fields that show up in the right panel for
 * Goal/Task/Subtask. Children (tasks/subTasks/checklist) are intentionally
 * excluded — those are diffed as their own tree rows, not as a field on the
 * parent. Returns true when *no field-level change* exists on this node alone.
 */
/** Compare two timestamps tolerantly: invalid-on-both-sides counts as equal,
 * not "different" (because NaN !== NaN would otherwise mark every legacy
 * record with bad dates as changed). */
const datesEqual = (a: string | number, b: string | number): boolean => {
  const ta = new Date(a).getTime()
  const tb = new Date(b).getTime()
  const aValid = !Number.isNaN(ta)
  const bValid = !Number.isNaN(tb)
  if (!aValid && !bValid) return true
  if (aValid !== bValid) return false
  return ta === tb
}

const goalLikeFieldsEqual = (
  a: { title: string; description: string; startDate: string | number; endDate: string | number; color?: string; baseColor?: string },
  b: { title: string; description: string; startDate: string | number; endDate: string | number; color?: string; baseColor?: string },
): boolean => {
  if (a.title !== b.title) return false
  if (a.description !== b.description) return false
  if (!datesEqual(a.startDate, b.startDate)) return false
  if (!datesEqual(a.endDate, b.endDate)) return false
  const colorA = (a.color ?? a.baseColor ?? '').toLowerCase()
  const colorB = (b.color ?? b.baseColor ?? '').toLowerCase()
  if (colorA !== colorB) return false
  return true
}

/** Compare two checklist entries on the fields editable from the nested view. */
const checklistFieldsEqual = (a: LegacyChecklistItem, b: LegacyChecklistItem): boolean => {
  if (a.text !== b.text) return false
  if (a.isCompleted !== b.isCompleted) return false
  if ((a.weightage ?? 1) !== (b.weightage ?? 1)) return false
  return true
}

/**
 * Build context passed down the tree-build recursion. Decouples node creation
 * from the various id sets we need to consult (unsaved, invalid, draft-attention)
 * so adding another flag later doesn't require threading another argument.
 */
export interface TreeBuildContext {
  unsavedIds: Set<string>
  invalidIds: Set<string>
  draftAttentionIds: Set<string>
  newNodeIds?: Set<string>
  editedNodeIds?: Set<string>
}

/**
 * Build a single tree node for the folder panel. `baseline` may be null when
 * the node is brand new (added by the AI); `working` may be null when the
 * node was removed by the AI. Both being null is not a valid call.
 */
const goalToTreeNode = (
  working: Goal | null,
  baseline: Goal | null,
  context: TreeBuildContext,
): TreeNode => {
  const present = working ?? baseline!
  let diff = computeNodeDiff(working, baseline, (w, b) => goalLikeFieldsEqual(w, b))
  if (context.newNodeIds?.has(present.id)) {
    diff = 'added'
  } else if (context.editedNodeIds?.has(present.id)) {
    diff = 'changed'
  }
  const tasks = mergeChildren(working?.tasks ?? [], baseline?.tasks ?? [])
    .map(({ w, b }) => taskToTreeNode(w, b, present.id, context))

  const checklistChildren = mergeChildren(working?.checklist ?? [], baseline?.checklist ?? [])
    .map(({ w, b }) => checklistToTreeNode(w, b, present.id, null, null, context))

  const children = [...tasks, ...checklistChildren]
  const hasDiffDescendant = children.some((child) => child.diff !== 'unchanged' || child.hasDiffDescendant)

  return {
    id: present.id,
    kind: 'goal',
    title: present.title || 'Untitled goal',
    diff,
    unsaved: context.unsavedIds.has(present.id),
    selectable: true,
    selection: { kind: 'goal', goalId: present.id },
    children,
    hasDiffDescendant,
    validation: context.invalidIds.has(present.id) ? 'invalid' : 'ok',
    needsDraftAttention: context.draftAttentionIds.has(present.id),
  }
}

const taskToTreeNode = (
  working: Task | null,
  baseline: Task | null,
  goalId: string,
  context: TreeBuildContext,
): TreeNode => {
  const present = working ?? baseline!
  let diff = computeNodeDiff(working, baseline, (w, b) => goalLikeFieldsEqual(w, b))
  if (context.newNodeIds?.has(present.id)) {
    diff = 'added'
  } else if (context.editedNodeIds?.has(present.id)) {
    diff = 'changed'
  }
  const subs = mergeChildren(working?.subTasks ?? [], baseline?.subTasks ?? [])
    .map(({ w, b }) => subTaskToTreeNode(w, b, goalId, present.id, context))

  const checklistChildren = mergeChildren(working?.checklist ?? [], baseline?.checklist ?? [])
    .map(({ w, b }) => checklistToTreeNode(w, b, goalId, present.id, null, context))

  const children = [...subs, ...checklistChildren]
  const hasDiffDescendant = children.some((child) => child.diff !== 'unchanged' || child.hasDiffDescendant)

  return {
    id: present.id,
    kind: 'task',
    title: present.title || 'Untitled task',
    diff,
    unsaved: context.unsavedIds.has(present.id),
    selectable: true,
    selection: { kind: 'task', goalId, taskId: present.id },
    children,
    hasDiffDescendant,
    validation: context.invalidIds.has(present.id) ? 'invalid' : 'ok',
    needsDraftAttention: context.draftAttentionIds.has(present.id),
  }
}

const subTaskToTreeNode = (
  working: SubTask | null,
  baseline: SubTask | null,
  goalId: string,
  taskId: string,
  context: TreeBuildContext,
): TreeNode => {
  const present = working ?? baseline!
  let diff = computeNodeDiff(working, baseline, (w, b) => goalLikeFieldsEqual(w, b))
  if (context.newNodeIds?.has(present.id)) {
    diff = 'added'
  } else if (context.editedNodeIds?.has(present.id)) {
    diff = 'changed'
  }

  const checklistChildren = mergeChildren(working?.checklist ?? [], baseline?.checklist ?? [])
    .map(({ w, b }) => checklistToTreeNode(w, b, goalId, taskId, present.id, context))

  const hasDiffDescendant = checklistChildren.some((child) => child.diff !== 'unchanged' || child.hasDiffDescendant)

  return {
    id: present.id,
    kind: 'subtask',
    title: present.title || 'Untitled subtask',
    diff,
    unsaved: context.unsavedIds.has(present.id),
    selectable: true,
    selection: { kind: 'subtask', goalId, taskId, subTaskId: present.id },
    children: checklistChildren,
    hasDiffDescendant,
    validation: context.invalidIds.has(present.id) ? 'invalid' : 'ok',
    needsDraftAttention: context.draftAttentionIds.has(present.id),
  }
}

const checklistToTreeNode = (
  working: LegacyChecklistItem | null,
  baseline: LegacyChecklistItem | null,
  goalId: string,
  taskId: string | null,
  subTaskId: string | null,
  context: TreeBuildContext,
): TreeNode => {
  const present = working ?? baseline!
  let diff = computeNodeDiff(working, baseline, (w, b) => checklistFieldsEqual(w, b))
  if (context.newNodeIds?.has(present.id)) {
    diff = 'added'
  } else if (context.editedNodeIds?.has(present.id)) {
    diff = 'changed'
  }

  return {
    id: present.id,
    kind: 'checklist',
    title: present.text || 'Untitled item',
    diff,
    unsaved: context.unsavedIds.has(present.id),
    selectable: true,
    selection: { kind: 'checklist', goalId, taskId, subTaskId, checklistId: present.id },
    children: [],
    hasDiffDescendant: false,
    validation: context.invalidIds.has(present.id) ? 'invalid' : 'ok',
    needsDraftAttention: context.draftAttentionIds.has(present.id),
  }
}

/** Three-way diff classification for a single node. */
const computeNodeDiff = <T>(
  working: T | null,
  baseline: T | null,
  fieldsEqual: (w: T, b: T) => boolean,
): NodeDiffStatus => {
  if (working && !baseline) return 'added'
  if (!working && baseline) return 'removed'
  if (working && baseline && !fieldsEqual(working, baseline)) return 'changed'
  return 'unchanged'
}

/**
 * Outer-join two lists of {id, …} on id, preserving the ordering of `working`
 * first then appending baseline-only entries. Each output row carries the
 * working and baseline versions (either may be null).
 */
const mergeChildren = <T extends { id: string }>(working: T[], baseline: T[]) => {
  const byIdBaseline = new Map(baseline.map((entry) => [entry.id, entry]))
  const seen = new Set<string>()
  const merged: Array<{ id: string; w: T | null; b: T | null }> = []

  for (const entry of working) {
    seen.add(entry.id)
    merged.push({ id: entry.id, w: entry, b: byIdBaseline.get(entry.id) ?? null })
  }
  for (const entry of baseline) {
    if (seen.has(entry.id)) continue
    merged.push({ id: entry.id, w: null, b: entry })
  }

  return merged
}

/**
 * Build the tree the left folder panel renders. In generate/detail mode the
 * baseline is null and every node renders as `unchanged`. In edit mode the
 * baseline is the pre-AI snapshot and diff statuses light up accordingly.
 */
export const buildTree = (
  workingGoal: Goal | null,
  baselineGoal: Goal | null,
  unsavedIds: Set<string>,
  invalidIds: Set<string> = new Set(),
  draftAttentionIds: Set<string> = new Set(),
  newNodeIds: Set<string> = new Set(),
  editedNodeIds: Set<string> = new Set(),
): TreeNode | null => {
  if (!workingGoal && !baselineGoal) return null
  return goalToTreeNode(workingGoal, baselineGoal, { unsavedIds, invalidIds, draftAttentionIds, newNodeIds, editedNodeIds })
}

/**
 * Multi-goal tree builder for `workspace` mode. Each goal pair (working +
 * baseline) becomes one top-level TreeNode. The ordering follows
 * `workingGoals` first, with baseline-only goals (goals the user removed in
 * this session — not a flow we use today but supported for symmetry)
 * appended at the end.
 */
export const buildWorkspaceTree = (
  workingGoals: Goal[],
  baselineGoals: Goal[],
  unsavedIds: Set<string>,
  invalidIds: Set<string> = new Set(),
  draftAttentionIds: Set<string> = new Set(),
  newNodeIds: Set<string> = new Set(),
  editedNodeIds: Set<string> = new Set(),
): TreeNode[] => {
  const context: TreeBuildContext = { unsavedIds, invalidIds, draftAttentionIds, newNodeIds, editedNodeIds }
  const byBaselineId = new Map(baselineGoals.map((goal) => [goal.id, goal]))
  const seen = new Set<string>()
  const roots: TreeNode[] = []
  for (const working of workingGoals) {
    seen.add(working.id)
    roots.push(goalToTreeNode(working, byBaselineId.get(working.id) ?? null, context))
  }
  for (const baseline of baselineGoals) {
    if (seen.has(baseline.id)) continue
    roots.push(goalToTreeNode(null, baseline, context))
  }
  return roots
}

/**
 * Resolve a selection to its working + baseline payload, regardless of which
 * one (or both) is present. Used by the right-side detail/diff panel.
 */
export const resolveSelection = (
  selection: DetailedSelection | null,
  workingGoal: Goal | null,
  baselineGoal: Goal | null,
): {
  workingNode: unknown
  baselineNode: unknown
} => {
  if (!selection) return { workingNode: null, baselineNode: null }

  const pickGoal = (goal: Goal | null) =>
    goal && goal.id === selection.goalId ? goal : null

  if (selection.kind === 'goal') {
    return { workingNode: pickGoal(workingGoal), baselineNode: pickGoal(baselineGoal) }
  }

  const pickTask = (goal: Goal | null, taskId: string) =>
    goal?.tasks.find((task) => task.id === taskId) ?? null

  if (selection.kind === 'task') {
    return {
      workingNode: pickTask(pickGoal(workingGoal), selection.taskId),
      baselineNode: pickTask(pickGoal(baselineGoal), selection.taskId),
    }
  }

  const pickSubTask = (goal: Goal | null, taskId: string, subTaskId: string) =>
    pickTask(goal, taskId)?.subTasks.find((sub) => sub.id === subTaskId) ?? null

  if (selection.kind === 'subtask') {
    return {
      workingNode: pickSubTask(pickGoal(workingGoal), selection.taskId, selection.subTaskId),
      baselineNode: pickSubTask(pickGoal(baselineGoal), selection.taskId, selection.subTaskId),
    }
  }

  // checklist
  const pickChecklist = (goal: Goal | null) => {
    if (!goal || goal.id !== selection.goalId) return null
    if (!selection.taskId) {
      return goal.checklist.find((item) => item.id === selection.checklistId) ?? null
    }
    const task = pickTask(goal, selection.taskId)
    if (!task) return null
    if (!selection.subTaskId) {
      return task.checklist.find((item) => item.id === selection.checklistId) ?? null
    }
    const sub = task.subTasks.find((s) => s.id === selection.subTaskId)
    return sub?.checklist.find((item) => item.id === selection.checklistId) ?? null
  }

  return {
    workingNode: pickChecklist(workingGoal),
    baselineNode: pickChecklist(baselineGoal),
  }
}

/**
 * Multi-goal variant of resolveSelection — picks the right goal out of an
 * array first, then resolves down to the requested kind.
 */
export const resolveSelectionInWorkspace = (
  selection: DetailedSelection | null,
  workingGoals: Goal[],
  baselineGoals: Goal[],
): { workingNode: unknown; baselineNode: unknown } => {
  if (!selection) return { workingNode: null, baselineNode: null }
  const workingGoal = workingGoals.find((goal) => goal.id === selection.goalId) ?? null
  const baselineGoal = baselineGoals.find((goal) => goal.id === selection.goalId) ?? null
  return resolveSelection(selection, workingGoal, baselineGoal)
}

/** Pure field-equality helpers exposed for the right-side DiffField wrapper. */
export const isFieldChanged = (
  workingValue: unknown,
  baselineValue: unknown,
): boolean => {
  if (workingValue === baselineValue) return false
  if (workingValue == null && baselineValue == null) return false
  if (workingValue instanceof Date && baselineValue instanceof Date) {
    return workingValue.getTime() !== baselineValue.getTime()
  }
  return JSON.stringify(workingValue ?? null) !== JSON.stringify(baselineValue ?? null)
}

/**
 * Walk goals -> tasks -> subtasks -> checklist[] to build the ancestor DetailedSelection chain.
 */
export const findSelectionForNodeId = (
  nodeId: string,
  nodeKind?: DetailedNodeKind | 'goal' | 'task' | 'subtask' | 'checklist',
  goals: Goal[] = [],
): DetailedSelection | null => {
  for (const goal of goals) {
    if ((!nodeKind || nodeKind === 'goal') && goal.id === nodeId) {
      return { kind: 'goal', goalId: goal.id }
    }

    if (!nodeKind || nodeKind === 'checklist') {
      const directItem = goal.checklist?.find((item) => item.id === nodeId)
      if (directItem) {
        return { kind: 'checklist', goalId: goal.id, taskId: null, subTaskId: null, checklistId: directItem.id }
      }
    }

    for (const task of goal.tasks ?? []) {
      if ((!nodeKind || nodeKind === 'task') && task.id === nodeId) {
        return { kind: 'task', goalId: goal.id, taskId: task.id }
      }

      if (!nodeKind || nodeKind === 'checklist') {
        const taskItem = task.checklist?.find((item) => item.id === nodeId)
        if (taskItem) {
          return { kind: 'checklist', goalId: goal.id, taskId: task.id, subTaskId: null, checklistId: taskItem.id }
        }
      }

      for (const subTask of task.subTasks ?? []) {
        if ((!nodeKind || nodeKind === 'subtask') && subTask.id === nodeId) {
          return { kind: 'subtask', goalId: goal.id, taskId: task.id, subTaskId: subTask.id }
        }

        if (!nodeKind || nodeKind === 'checklist') {
          const subItem = subTask.checklist?.find((item) => item.id === nodeId)
          if (subItem) {
            return { kind: 'checklist', goalId: goal.id, taskId: task.id, subTaskId: subTask.id, checklistId: subItem.id }
          }
        }
      }
    }
  }

  return null
}

/**
 * Convert a flat element id (Goal/Task/Subtask) to a DetailedSelection by
 * looking up the element and its ancestor chain in `elements`.
 * Returns null when the element is missing or is a checklist item
 * (checklist selection needs a different constructor — handle upstream).
 */
export const elementIdToDetailedSelection = (
  elementId: string | null,
  elements: Array<{ id: string; type: string; parentId?: string | null }>,
): DetailedSelection | null => {
  if (!elementId) return null
  const byId = new Map(elements.map((e) => [e.id, e]))
  const node = byId.get(elementId)
  if (!node) return null
  if (node.type === 'GOAL') return { kind: 'goal', goalId: node.id }
  if (node.type === 'TASK' && node.parentId) {
    return { kind: 'task', goalId: node.parentId, taskId: node.id }
  }
  if (node.type === 'SUBTASK' && node.parentId) {
    const parentTask = byId.get(node.parentId)
    if (parentTask?.parentId) {
      return { kind: 'subtask', goalId: parentTask.parentId, taskId: parentTask.id, subTaskId: node.id }
    }
  }
  return null
}
